<template>
  <div class="availability-form-weekly">
    <div class="space-y-2">
      <!-- Title / Main switch -->
      <div class="flex items-center justify-between gap-2">
        <span class="text-sm font-semibold select-none">Wekelijkse beschikbaarheid</span>
        <PSwitch
          v-model="isAssignWeeklyAvailability"
          :disabled="disabled"
          @update:model-value="onAssignWeeklyAvailabilityChanged"
          title="Wekelijkse beschikbaarheid"
        />
      </div>

      <!-- Days list -->
      <Transition name="fade">
        <div
          v-if="isAssignWeeklyAvailability"
          class="border-surface space-y-2 rounded-md border-2 p-2"
        >
          <div v-for="(raPerDay, i) in recurringAvailabilitiesPerDay" :key="i">
            <AvailabilityFormDaily
              v-model="recurringAvailabilitiesPerDay[i]"
              :key="i"
              :disabled="disabled || !isAssignWeeklyAvailability"
              @add-recurring-availability="
                (recurringAvailability: RecurringAvailability) =>
                  addRecurringAvailability(i, recurringAvailability)
              "
              @remove-recurring-availability="
                (_recurringAvailability: RecurringAvailability, slotIndex: number) =>
                  removeRecurringAvailability(i, slotIndex)
              "
            />
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { inject, ref, watch } from "vue"
import PSwitch from "@/components/form/PSwitch.vue"
import AvailabilityFormDaily from "@/components/model_edit_form/availability/AvailabilityFormDaily.vue"
import type { AvailabilityBucket } from "@/models/availability-bucket.model.ts"
import { Weekdays } from "@/enums/models/weekdays.ts"
import { RecurringAvailability } from "@/models/recurring-availability.model.ts"
import type { IRecurringAvailability } from "@/interfaces/api/recurring-availability.interface.ts"
import { AvailabilityTypes } from "@/enums/models/availability-types.ts"
import { ModelFactory } from "@/factories/model-factory.ts"
import type { RecurringAvailabilityController } from "@/controllers/model-controllers/recurring-availability-controller.ts"

const props = withDefaults(
  defineProps<{
    availabilityBucket: AvailabilityBucket
    disabled?: boolean
  }>(),
  {
    disabled: false
  }
)

function createRecurringAvailability(
  dayOfWeek: Weekdays,
  availabilityBucket = props.availabilityBucket.id
): RecurringAvailability {
  return ModelFactory.createModel(RecurringAvailability, {
    availabilityBucket,
    dayOfWeek,
    availabilityType: AvailabilityTypes.AVAILABLE,
    isActive: true
  } as IRecurringAvailability)
}

function buildWeekdays(): RecurringAvailability[][] {
  const perDay: Record<Weekdays, RecurringAvailability[]> = {
    [Weekdays.MONDAY]: [],
    [Weekdays.TUESDAY]: [],
    [Weekdays.WEDNESDAY]: [],
    [Weekdays.THURSDAY]: [],
    [Weekdays.FRIDAY]: [],
    [Weekdays.SATURDAY]: [],
    [Weekdays.SUNDAY]: []
  }

  // Add existing recurring availabilities to corresponding days.
  for (const ra of props.availabilityBucket.recurringAvailabilities) {
    perDay[ra.dayOfWeek].push(ra)
  }

  // Days without recurring availabilities might remain, fill them with one recurring availability.
  for (const [dayOfWeek, day] of Object.entries(perDay)) {
    if (day.length === 0) {
      const recurringAvailability = createRecurringAvailability(Number(dayOfWeek))
      day.push(recurringAvailability)
    }
  }
  return Object.values(perDay)
}

const recurringAvailabilitiesPerDay = ref<RecurringAvailability[][]>(buildWeekdays())
watch(
  () => props.availabilityBucket.recurringAvailabilities.map((ra) => ra.id),
  () => {
    recurringAvailabilitiesPerDay.value = buildWeekdays()
  }
)

function addRecurringAvailability(dayIndex: number, recurringAvailability: RecurringAvailability) {
  const daySlots = recurringAvailabilitiesPerDay.value[dayIndex]
  const newRecurringAvailability = createRecurringAvailability(
    recurringAvailability.dayOfWeek,
    recurringAvailability.availabilityBucket
  )
  daySlots.push(newRecurringAvailability)
}

function removeRecurringAvailability(dayIndex: number, insertAtIndex: number) {
  const daySlots = recurringAvailabilitiesPerDay.value[dayIndex]

  daySlots.splice(insertAtIndex, 1)
}
const isAssignWeeklyAvailability = ref(
  // Default to true if any recurring availability is active and has an availabilityType of UNAVAILABLE or has timeStart or timeEnd.
  props.availabilityBucket.recurringAvailabilities.some(
    (recurringAvailability) =>
      recurringAvailability.isActive &&
      (recurringAvailability.availabilityType === AvailabilityTypes.UNAVAILABLE ||
        recurringAvailability.timeStart ||
        recurringAvailability.timeEnd)
  )
)

const recurringAvailabilityController = inject<RecurringAvailabilityController>(
  "recurringAvailabilityController"
)!

function onAssignWeeklyAvailabilityChanged(isEnabled: boolean) {
  for (const recurringAvailability of props.availabilityBucket.recurringAvailabilities) {
    recurringAvailability.isActive = isEnabled
  }
  const savedRecurringAvailabilities = props.availabilityBucket.recurringAvailabilities.filter(
    ({ isSaved }) => isSaved
  )
  if (savedRecurringAvailabilities.length > 0) {
    recurringAvailabilityController.bulkUpdate(savedRecurringAvailabilities)
  }
}
</script>

<style scoped></style>
